/**
 * A minimum margin every quotation line must clear before the quotation is created.
 *
 * The requirement, and the tension inside it, are set out in this module's test: one floor for ALL
 * lines, checked at CREATION, switchable off entirely, and overridable when a customer negotiates
 * a discount — hard, but not immovable.
 *
 * ---- What this module decides, and what it does not ----
 *
 * It answers only "which lines are under the floor, and by how much". It does not decide whether
 * that blocks anything: `enforce` in Settings governs that, and the override belongs to the screen
 * where a human can take responsibility for it. Keeping the arithmetic separate from the policy is
 * what lets the same numbers drive a warning in one place and a block in another.
 *
 * ---- Distinct from `marginGate.ts` ----
 *
 * That one blocks SENDING a quotation with a line at or below cost — an unconditional floor of
 * zero, and a different question with a different answer. This one is a configurable business
 * floor above cost. A line can clear this and fail that, or the reverse.
 */

import type { QuotationLinePayload } from './quotationFromAllocation'

export interface FloorBreach {
  item_code: string
  item_name: string
  enquiry_row_no: string
  /** The line's actual margin, as a percentage over cost. */
  pct: number
}

/**
 * Markup over cost, as a percentage, or `null` when it cannot be computed.
 *
 * `null` rather than `Infinity` for an unknown cost: a percentage over nothing is not a number,
 * and `Infinity` would rank the line as the healthiest on the quotation. The same reading of a
 * zero cost that `marginGate.ts` uses — "we were never told", not "it was free".
 */
export function marginPctOf(line: QuotationLinePayload): number | null {
  const cost = line.cost_unit
  if (!Number.isFinite(cost) || cost <= 0) return null
  return ((line.sell_unit - cost) / cost) * 100
}

/** Rounded the way the UI shows it, so a line reading 15% is never reported as breaching 15%. */
const round2 = (n: number): number => Math.round(n * 100) / 100

/**
 * Every line under `floorPct`, in order.
 *
 * A floor of 0, `undefined` or negative means there is no floor — that is how the feature is
 * DISABLED, and it must be inert rather than degenerating into "must beat cost" (which is
 * `marginGate.ts`'s separate, unconditional job).
 *
 * A line whose cost is unknown is SKIPPED, not reported: we cannot show it is under, and blocking
 * a quotation over a number we do not have would be a floor nobody could satisfy.
 */
export function linesBelowFloor(
  lines: readonly QuotationLinePayload[],
  floorPct: number | undefined,
): FloorBreach[] {
  if (floorPct === undefined || !Number.isFinite(floorPct) || floorPct <= 0) return []

  const out: FloorBreach[] = []
  for (const line of lines) {
    const pct = marginPctOf(line)
    if (pct === null) continue
    // Compared on the rounded figure: a line the screen shows as exactly 15% must not breach 15%
    // on a float artefact three decimals down.
    if (round2(pct) < floorPct) {
      out.push({
        item_code: line.item_code,
        item_name: line.item_name,
        enquiry_row_no: line.enquiry_row_no,
        pct: round2(pct),
      })
    }
  }
  return out
}

/**
 * What the purchaser reads before deciding whether to override.
 *
 * Names the item and both numbers. Someone about to accept a discount needs to know what they are
 * agreeing to, not merely that something is wrong — a message that only counts breaches turns the
 * override into a reflex.
 */
export function describeFloorBreaches(breaches: readonly FloorBreach[], floorPct: number): string | null {
  if (breaches.length === 0) return null

  const named = breaches.slice(0, 3).map((b) => `${b.item_code} at ${b.pct}%`)
  const rest = breaches.length - named.length

  if (breaches.length === 1) {
    return `${named[0]} — below the ${floorPct}% minimum margin.`
  }
  return `${breaches.length} lines are below the ${floorPct}% minimum margin: ${named.join(', ')}${
    rest > 0 ? `, and ${rest} more` : ''
  }.`
}

/** The configured floor, or `undefined` when no `minMarkup` rule is set. */
export function floorFromRules(rules: readonly { kind: string; pct?: number }[]): number | undefined {
  // The strictest wins if somebody configures two — a floor that a second rule could relax would
  // not be a floor.
  const pcts = rules.filter((r) => r.kind === 'minMarkup' && typeof r.pct === 'number').map((r) => r.pct as number)
  return pcts.length === 0 ? undefined : Math.max(...pcts)
}
